import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	ClassNameProps,
	Column,
	compareBooleansDesc,
	compareObjects,
	Conditional,
	Else,
	If,
	jsxJoin,
	omitKeys,
	Rule,
	StyleProps,
} from "@hex-insights/core";
import { EmptyView } from "../../../../../Components/Empty";
import { Icon } from "../../../../../Components/Icon";
import { SearchField } from "../../../../../Components/Search";
import { User } from "../../../../../Utilities";
import { UserFilterFormState, UserFilterFormUtils } from "../../../../Filter";
import {
	SelectRecipientSection,
	splitUserGroups,
	UserGroupRecipientSection,
	UserRecipientList,
	UserRecipientSection,
} from "../Recipients";
import { useRecipients } from "./utils";
import styles from "./recipients.module.css";

export type MessageRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
	onClickUserGroupRecipient: (id: User["id"]) => void;
	selectedUserGroupIDs?: User["id"][];
	withSelected?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function MessageRecipientSections({
	onClickUserRecipient,
	selectedUserIDs,
	onClickUserGroupRecipient,
	selectedUserGroupIDs,
	withSelected = false,
	className,
	style,
}: MessageRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const userFilterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	const finalUserFilterInput = omitKeys(userFilterInput, ["search"]);
	const { loading: loadingRecipients, data: recipientData } = useRecipients(
		userFilterInput.search,
		finalUserFilterInput,
	);
	const { data: allRecipientData } = useRecipients("", finalUserFilterInput);
	const isLoading = loadingRecipients;
	const allUsers = React.useMemo(
		() => allRecipientData?.userConnection.edges.map((e) => e.node) ?? [],
		[allRecipientData],
	);

	const users = React.useMemo(() => {
		return recipientData?.userConnection.edges.map((e) => e.node) ?? [];
	}, [recipientData]);
	const allUserGroups = React.useMemo(
		() => allRecipientData?.userGroupConnection.edges.map((e) => e.node) ?? [],
		[allRecipientData],
	);
	const { viewerGroups, otherGroups } = React.useMemo(() => splitUserGroups(recipientData), [recipientData]);

	const sections = [];
	if (users.length > 0) {
		sections.push(
			<UserRecipientSection
				key="Users"
				name="Users"
				users={users}
				onClickRecipient={onClickUserRecipient}
				selectedUserIDs={selectedUserIDs}
			/>,
		);
	}
	if (viewerGroups.length > 0) {
		sections.push(
			<UserGroupRecipientSection
				key="Your Groups"
				name="Your Groups"
				userGroups={viewerGroups}
				onClickRecipient={onClickUserGroupRecipient}
				selectedUserGroupIDs={selectedUserGroupIDs}
			/>,
		);
	}
	if (otherGroups.length > 0) {
		sections.push(
			<UserGroupRecipientSection
				key="Other Groups"
				name="Other Groups"
				userGroups={otherGroups}
				onClickRecipient={onClickUserGroupRecipient}
				selectedUserGroupIDs={selectedUserGroupIDs}
			/>,
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			<If
				condition={
					withSelected &&
					(sections.length > 0 || (selectedUserIDs?.length ?? 0) > 0 || (selectedUserGroupIDs?.length ?? 0) > 0)
				}
			>
				<SelectRecipientSection
					users={allUsers}
					onClickUserRecipient={onClickUserRecipient}
					selectedUserIDs={selectedUserIDs}
					userGroups={allUserGroups}
					onClickUserGroupRecipient={onClickUserGroupRecipient}
					selectedUserGroupIDs={selectedUserGroupIDs}
					className={styles["selected-recipient-section"]}
				/>
			</If>
			<Conditional>
				<If condition={sections.length > 0}>{jsxJoin(sections, <Rule thickness="2px" />)}</If>
				<If condition={isLoading}>Loading...</If>
				<Else>
					<EmptyView
						IconComponent={Icon.Users}
						iconSize={48}
						primaryText="Recipients"
						secondaryText="No relevant recipients to show."
					/>
				</Else>
			</Conditional>
		</Column>
	);
}

const defaultSelectedUserIDs: User["id"][] = [];

export type MessageEditRecipientSectionsProps = {
	onClickUserRecipient: (id: User["id"]) => void;
	selectedUserIDs?: User["id"][];
} & Partial<ClassNameProps & StyleProps>;

export function MessageEditRecipientSections({
	onClickUserRecipient,
	selectedUserIDs = defaultSelectedUserIDs,
	className,
	style,
}: MessageEditRecipientSectionsProps) {
	const userFilterFormState = UserFilterFormState.useFilterFormState();
	const userFilterInput = IndexForms.useFilterInput(userFilterFormState.formValues, UserFilterFormUtils.toFilterInputs);
	const { loading: loadingRecipients, data: recipientData } = useRecipients(userFilterInput.search, userFilterInput);

	const isLoading = loadingRecipients;
	const [initialSelectedUserIDs] = React.useState(selectedUserIDs);
	const users = React.useMemo(() => {
		return (
			recipientData?.userConnection.edges
				.map((e) => e.node)
				.sort(compareObjects((e) => initialSelectedUserIDs.includes(e.id), compareBooleansDesc())) ?? []
		);
	}, [recipientData, initialSelectedUserIDs]);

	return (
		<Column justify="spaced-start" verticalSpacing="1rem" className={className} style={style}>
			<SearchField formState={userFilterFormState} name="search" />

			<Conditional>
				<If condition={users.length > 0}>
					<UserRecipientList
						users={users}
						selectedUserIDs={selectedUserIDs}
						onClickUserRecipient={onClickUserRecipient}
						paginationSyncID={userFilterFormState.formValues.search}
					/>
				</If>
				<If condition={isLoading}>Loading...</If>
				<Else>
					<EmptyView
						IconComponent={Icon.Users}
						iconSize={48}
						primaryText="Recipients"
						secondaryText="No relevant recipients to show."
					/>
				</Else>
			</Conditional>
		</Column>
	);
}
